import React, { useState, useRef, useEffect } from "react";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import { SidebarItems } from "./SidebarItems";
import type { SidebarItem } from "./SidebarItems";
import { SidebarDropdown } from "./SidebarDropdown";
import type { DropdownItem } from "./SidebarDropdown";
import { QuotaProgressBar } from "./QuotaProgressBar";

/// The allowance closest to full, already formatted — bytes and counts read
/// differently, and only the caller knows which it holds.
export interface RailMeter {
  label: string;
  usedText: string;
  percentage: number;
  statusMessage?: string;
}

interface StorageIndicatorProps {
  isOpen: boolean;
  meter: RailMeter;
  onSupportClick: (anchorTop?: number) => void;
  sidebarWidth: string;
}

export const StorageIndicator: React.FC<StorageIndicatorProps> = ({
  isOpen,
  meter,
  onSupportClick,
  sidebarWidth,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setShowDropdown(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [showDropdown]);

  const { percentage, usedText } = meter;

  const iconColor =
    percentage >= 90
      ? "var(--color-error)"
      : percentage >= 70
        ? "var(--color-warning)"
        : undefined;

  const tooltipText = `${meter.label}: ${usedText} (${Math.round(percentage)}%)`;

  const customContent = (
    <div className="w-full flex flex-col gap-1">
      <span
        className="text-fg-muted-regular-small truncate"
        style={{ fontFamily: "var(--font-mono)" }}
      >
        {usedText}
      </span>
      <QuotaProgressBar percentage={percentage} />
    </div>
  );

  const handleClick = () => {
    if (buttonRef.current) {
      setDropdownTop(buttonRef.current.getBoundingClientRect().top);
    }
    setShowDropdown((prev) => !prev);
  };

  const handleDropdownHoverChange = (hovered: boolean) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    if (!hovered) {
      dropdownTimeoutRef.current = setTimeout(
        () => setShowDropdown(false),
        150,
      );
    }
  };

  const statusMessage = meter.statusMessage;

  const headerContent = (
    <div className="px-3 py-2.5 border-b border-border">
      <div className="text-mono-small mb-2">
        {meter.label} — {meter.usedText} used ({Math.round(percentage)}%)
      </div>
      <div className="mb-2">
        <QuotaProgressBar percentage={percentage} size="md" />
      </div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block px-2 py-px rounded-full text-fg-muted-medium-xs"
          style={{ background: "var(--color-bg-hover)" }}
        >
          Alpha
        </span>
        {statusMessage && (
          <span
            style={{
              fontSize: "var(--font-size-x-small)",
              color:
                percentage >= 90
                  ? "var(--color-error)"
                  : "var(--color-warning)",
            }}
          >
            {statusMessage}
          </span>
        )}
      </div>
    </div>
  );

  const dropdownItems: DropdownItem[] = [
    {
      id: "support",
      label: "Contact Support",
      icon: <HelpOutlineIcon className="icon-xs" />,
      onClick: () => {
        setShowDropdown(false);
        onSupportClick(dropdownTop);
      },
    },
  ];

  const sidebarItem: SidebarItem = {
    icon: (
      <CloudOutlinedIcon
        className="icon-md cursor-default"
        style={{ color: iconColor }}
      />
    ),
    text: meter.label,
    tooltip: tooltipText,
    customContent,
    onClick: handleClick,
    buttonRef,
  };

  return (
    <>
      <SidebarItems
        items={[sidebarItem]}
        isOpen={isOpen}
        variant="profile"
        sidebarWidth={sidebarWidth}
      />
      <div
        ref={wrapperRef}
        className="contents"
        onMouseEnter={() => {
          if (dropdownTimeoutRef.current)
            clearTimeout(dropdownTimeoutRef.current);
        }}
        onMouseLeave={() => setShowDropdown(false)}
      >
        <SidebarDropdown
          items={dropdownItems}
          isVisible={showDropdown}
          topPosition={dropdownTop}
          sidebarWidth={sidebarWidth}
          headerContent={headerContent}
          onHoverChange={handleDropdownHoverChange}
        />
      </div>
    </>
  );
};
